import React, { useRef, useState } from 'react';
import { Icon } from '../icons';
import { IconButton } from './Button';
import { useT } from '../../i18n';

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * FileUpload — drag-and-drop dropzone + file list (controlled).
 *
 * Props:
 *   files: File[]                 — current files
 *   onChange(File[])
 *   accept: string                — input accept attr
 *   multiple: boolean (default true)
 *   maxSizeMB: number             — rejects larger files
 *   hint: ReactNode
 *   disabled
 */
const FileUpload = ({
  files = [],
  onChange,
  accept,
  multiple = true,
  maxSizeMB,
  hint,
  disabled = false,
  className = '',
}) => {
  const t = useT();
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [rejected, setRejected] = useState([]);

  const accept_ = (incoming) => {
    let list = Array.from(incoming);
    if (maxSizeMB) {
      const limit = maxSizeMB * 1024 * 1024;
      // Dropping a file on the floor without saying why reads as a broken control.
      setRejected(list.filter((f) => f.size > limit).map((f) => ({ name: f.name, size: f.size })));
      list = list.filter((f) => f.size <= limit);
    }
    if (!multiple) list = list.slice(0, 1);
    onChange?.(multiple ? [...files, ...list] : list);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    accept_(e.dataTransfer.files);
  };

  const remove = (i) => onChange?.(files.filter((_, idx) => idx !== i));

  return (
    <div className={`ps-upload ${className}`.trim()}>
      <button
        type="button"
        className={`ps-upload__zone${dragging ? ' is-dragging' : ''}`}
        disabled={disabled}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <Icon.Upload size={22} className="ps-upload__icon" />
        <span className="ps-upload__primary">
          <span className="ps-upload__link">{t('Click to upload')}</span> {t('or drag and drop')}
        </span>
        {hint && <span className="ps-upload__hint">{hint}</span>}
        <input
          ref={inputRef}
          type="file"
          className="ps-upload__input"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => { accept_(e.target.files); e.target.value = ''; }}
        />
      </button>

      {rejected.length > 0 && (
        <ul className="ps-upload__rejected">
          {rejected.map((f, i) => (
            <li key={`${f.name}-${i}`}>
              {t('{name} is {size} — over the {max} MB limit and was not added.', {
                name: f.name, size: formatSize(f.size), max: maxSizeMB,
              })}
            </li>
          ))}
        </ul>
      )}

      {files.length > 0 && (
        <ul className="ps-upload__list">
          {files.map((f, i) => (
            <li key={`${f.name}-${i}`} className="ps-upload__file">
              <Icon.FileText size={16} className="ps-upload__file-icon" />
              <span className="ps-upload__file-name">{f.name}</span>
              <span className="ps-upload__file-size">{formatSize(f.size)}</span>
              <IconButton aria-label={t('Remove {name}', { name: f.name })} variant="ghost" size="icon-sm" onClick={() => remove(i)}>
                <Icon.X />
              </IconButton>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FileUpload;
